import type { IconType } from 'react-icons';
import {
  LuSettings,
  LuLogs,
  LuUser,
  LuFileText,
  LuShield,
  LuUpload,
} from 'react-icons/lu';
import { IoEyeOutline } from 'react-icons/io5';
import { MdOutlineDashboardCustomize } from 'react-icons/md';

/**
 * Centralized page configuration for the application.
 * Single source of truth for:
 * - Sidebar navigation items
 * - Header titles and custom components
 * - Path matching logic
 */

export type CustomHeaderId = 'patient-header';

type HeaderConfig =
  | { type: 'title' }
  | { type: 'custom'; componentId: CustomHeaderId };

export interface PageConfig {
  id: string;
  path: string;
  pathMatch: 'exact' | 'prefix' | 'suffix';
  pathSuffix?: string;
  title: string;
  sidebar?: {
    icon: IconType;
    order: number;
  };
  header: HeaderConfig;
}

/**
 * Application pages configuration.
 * Order matters for path matching when using findPageByPath.
 */
export const PAGES: readonly PageConfig[] = [
  // Reader (studies/worklist)
  {
    id: 'studies',
    path: '/studies',
    pathMatch: 'exact',
    title: 'Reader',
    sidebar: { icon: IoEyeOutline, order: 1 },
    header: { type: 'title' },
  },
  {
    id: 'study-detail',
    path: '/studies',
    pathMatch: 'prefix',
    title: 'Reader',
    header: { type: 'custom', componentId: 'patient-header' },
  },
  // Patients
  {
    id: 'patients',
    path: '/patients',
    pathMatch: 'exact',
    title: 'Patients',
    sidebar: { icon: LuUser, order: 2 },
    header: { type: 'title' },
  },
  {
    id: 'patient-detail',
    path: '/patients',
    pathMatch: 'prefix',
    title: 'Patient Detail',
    header: { type: 'title' },
  },
  // Reports
  {
    id: 'reports',
    path: '/reports',
    pathMatch: 'exact',
    title: 'Reports',
    sidebar: { icon: LuFileText, order: 3 },
    header: { type: 'title' },
  },
  {
    id: 'report-detail',
    path: '/reports',
    pathMatch: 'prefix',
    title: 'Report Detail',
    header: { type: 'title' },
  },
  // Templates
  {
    id: 'templates',
    path: '/templates',
    pathMatch: 'prefix',
    title: 'Templates',
    sidebar: { icon: MdOutlineDashboardCustomize, order: 4 },
    header: { type: 'title' },
  },
  // Uploads
  {
    id: 'uploads',
    path: '/uploads',
    pathMatch: 'exact',
    title: 'Uploads',
    sidebar: { icon: LuUpload, order: 5 },
    header: { type: 'title' },
  },
  // Worklist
  {
    id: 'worklist',
    path: '/worklist',
    pathMatch: 'exact',
    title: 'Worklist',
    sidebar: { icon: LuLogs, order: 6 },
    header: { type: 'title' },
  },
  // Admin
  {
    id: 'admin',
    path: '/admin',
    pathMatch: 'prefix',
    title: 'Admin',
    sidebar: { icon: LuShield, order: 7 },
    header: { type: 'title' },
  },
  // Settings
  {
    id: 'settings',
    path: '/settings',
    pathMatch: 'prefix',
    title: 'Settings',
    sidebar: { icon: LuSettings, order: 8 },
    header: { type: 'title' },
  },
];

// Type for sidebar items (pages that have sidebar config)
export type SidebarPageConfig = PageConfig & {
  sidebar: NonNullable<PageConfig['sidebar']>;
};

/**
 * Returns pages that should appear in the sidebar, sorted by order.
 */
export const getSidebarPages = (): SidebarPageConfig[] =>
  PAGES.filter((page): page is SidebarPageConfig => !!page.sidebar).sort(
    (a, b) => a.sidebar.order - b.sidebar.order
  );

/**
 * Finds the page configuration matching the given pathname.
 * Match priority is controlled by array order in PAGES.
 */
export const findPageByPath = (pathname: string): PageConfig | undefined =>
  PAGES.find((page) => {
    if (page.pathMatch === 'exact') {
      return pathname === page.path;
    }
    if (page.pathMatch === 'suffix') {
      const startsWithPath =
        pathname === page.path || pathname.startsWith(`${page.path}/`);
      return (
        startsWithPath && page.pathSuffix && pathname.endsWith(page.pathSuffix)
      );
    }
    // Prefix match: '/admin' matches '/admin' and '/admin/users'
    return pathname === page.path || pathname.startsWith(`${page.path}/`);
  });
